/**
 * Multi-note feed for a Books & Records item (mirrors job-contact notes):
 * a list of timestamped notes with author + delete, plus a composer.
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { formatDistanceToNow } from "date-fns";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import { Trash2 } from "lucide-react";

export function ItemNotesFeed({
  itemId,
  canEdit,
}: {
  itemId: Id<"booksAndRecordsItems">;
  canEdit: boolean;
}) {
  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.booksAndRecords.notes.listItemNotes,
      { itemId },
    ),
  );
  const create = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.notes.createItemNote,
    ),
  });
  const remove = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.notes.deleteItemNote,
    ),
  });

  const notes = data ?? [];
  const [draft, setDraft] = useState("");

  const post = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await create.mutateAsync({ itemId, content });
      setDraft("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add note");
    }
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note…"
            className="min-h-[68px]"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={post}
              disabled={!draft.trim() || create.isPending}
            >
              Add Note
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li
              key={n._id}
              className="group rounded-md border bg-muted/30 p-3 text-sm"
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {n.authorName}
                  </span>
                  <span>
                    {formatDistanceToNow(n.createdAt, { addSuffix: true })}
                  </span>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => remove.mutate({ noteId: n._id })}
                    className="text-muted-foreground/50 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    aria-label="Delete note"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
              <p className="whitespace-pre-wrap leading-relaxed text-foreground">
                {n.content}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
